// Header.js
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  ShoppingBag,
  User,
  Menu,
  X,
  Heart,
  MoreVertical,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useGuest } from "../../context/GuestContext";
import logo from "../../assets/logo.png";
import { motion, AnimatePresence } from "framer-motion";
import TrackOrderButton from "../common/TrackOrderButton";
import "./Header.css";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: -12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: "easeOut" },
  },
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isKebabOpen, setIsKebabOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { isAuthenticated, user, logout } = useAuth();
  const { getCartItemCount } = useCart();
  const { guestItemCount } = useGuest();
  const navigate = useNavigate();

  // ------------------------------
  // Refs for outside click closing
  // ------------------------------
  const menuRef = useRef(null);
  const kebabRef = useRef(null);
  const searchRef = useRef(null);

  // -------------------------------------------
  // Prevent body scroll when menu is open
  // -------------------------------------------
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // -------------------------------------------
  // Detect outside clicks for menus & search
  // -------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close hamburger menu
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }

      // Close kebab menu
      if (
        isKebabOpen &&
        kebabRef.current &&
        !kebabRef.current.contains(event.target)
      ) {
        setIsKebabOpen(false);
      }

      // Close search overlay
      if (
        isSearchOpen &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen, isKebabOpen, isSearchOpen]);

  // -------------------------------------------
  // Search handling
  // -------------------------------------------
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setIsSearchOpen(false);
      setIsKebabOpen(false);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setIsKebabOpen(false);
  };

  const cartItemCount = isAuthenticated ? getCartItemCount() : guestItemCount;

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          {/* Hamburger Menu */}
          <motion.button
            className="mobile-menu-btn"
            onClick={toggleMenu}
            whileTap={{ scale: 0.9 }}
            whileHover={{ rotate: 5 }}
            aria-label="Toggle Navigation"
          >
            {isMenuOpen ? (
              <X size={24} strokeWidth={2.5} />
            ) : (
              <Menu size={24} strokeWidth={2.5} />
            )}
          </motion.button>

          {/* Logo */}
          <Link to="/" className="logo">
            <img src={logo} alt="Lilyth" className="logo-image" />
          </Link>

          <div className="header-spacer"></div>

          {/* Header Actions */}
          <div className="header-actions">
            {/* Desktop Icons */}
            <div className="desktop-icons">
              {/* Search */}
              <button
                className="action-btn search-btn"
                onClick={toggleSearch}
                aria-label="Search"
              >
                <Search size={22} />
              </button>

              {/* Wishlist */}
              {isAuthenticated && (
                <Link
                  to="/account/wishlist"
                  className="action-btn"
                  aria-label="Wishlist"
                >
                  <Heart size={22} />
                </Link>
              )}

              {/* Cart */}
              <Link
                to="/cart"
                className="action-btn cart-btn"
                aria-label="Cart"
              >
                <ShoppingBag size={22} />
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </Link>

              {/* User */}
              {isAuthenticated ? (
                <div className="user-menu">
                  <div className="user-dropdown">
                    <button
                      className="action-btn user-btn user-expandable"
                      aria-label="User Account"
                    >
                      <span className="user-icon">
                        <User size={22} />
                      </span>
                      <span className="user-name-expand">
                        {user?.firstName}
                      </span>
                    </button>
                    <div className="dropdown-menu">
                      <Link to="/account" className="dropdown-item">
                        Account
                      </Link>
                      <Link to="/account/orders" className="dropdown-item">
                        Orders
                      </Link>
                      <Link to="/account/wishlist" className="dropdown-item">
                        Wishlist
                      </Link>
                      {user?.role === "admin" && (
                        <div className="admin-controls">
                          <Link to="/admin" className="dropdown-item">
                            Admin Panel
                          </Link>
                          <Link to="/admin/test-mode" className="dropdown-item">
                            Test as Customer
                          </Link>
                        </div>
                      )}
                      <button
                        onClick={logout}
                        className="dropdown-item logout-btn"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="action-btn" aria-label="Login">
                  <User size={22} />
                </Link>
              )}
            </div>

            {/* Mobile Kebab Menu */}
            <div className="mobile-icons" ref={kebabRef}>
              <button
                className="action-btn"
                onClick={() => setIsKebabOpen(!isKebabOpen)}
                aria-label={isAuthenticated ? "User Menu" : "More Options"}
              >
                {isAuthenticated ? (
                  <User size={22} />
                ) : (
                  <MoreVertical size={22} />
                )}
              </button>

              {isKebabOpen && (
                <div className="kebab-dropdown">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      toggleSearch();
                      setIsKebabOpen(false);
                    }}
                  >
                    <Search size={20} /> Search
                  </button>

                  <Link
                    to="/cart"
                    className="dropdown-item"
                    onClick={() => setIsKebabOpen(false)}
                  >
                    <ShoppingBag size={20} /> Cart ({cartItemCount})
                  </Link>

                  {isAuthenticated ? (
                    <Link
                      to="/account"
                      className="dropdown-item"
                      onClick={() => setIsKebabOpen(false)}
                    >
                      <User size={20} /> Account
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      className="dropdown-item"
                      onClick={() => setIsKebabOpen(false)}
                    >
                      <User size={20} /> Sign In
                    </Link>
                  )}

                  {/* Logout (ONLY for logged in users) */}
                  {isAuthenticated && (
                    <button
                      className="dropdown-item logout-item"
                      onClick={() => {
                        logout();
                        setIsKebabOpen(false);
                      }}
                    >
                      <User size={20} /> Logout
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        {isSearchOpen && (
          <div className="search-overlay" ref={searchRef}>
            <form onSubmit={handleSearch}>
              <div className="search-input-wrapper">
                <Search className="search-icon" size={20} />
                <input
                  type="text"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={toggleSearch}
                  className="close-search-btn"
                >
                  <X size={24} />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ---------------------------- */}
        {/* CASCADING MENU (NO SLIDE) */}
        {/* ---------------------------- */}

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              key="mobile-nav"
              ref={menuRef}
              className="mobile-nav open"
              initial="hidden"
              animate="show"
              exit="hidden"
              variants={containerVariants}
            >
              {/* SHOP SECTION */}
              <motion.div
                variants={itemVariants}
                className="mobile-nav-section"
              >
                <h3 className="mobile-nav-title">Shop</h3>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/shop"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    All Items
                  </Link>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/shop?category=indo-western"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    Indo-Western
                  </Link>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/shop?category=ethnicwear"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    Ethnicwear
                  </Link>
                </motion.div>
              </motion.div>

              {/* ACCOUNT SECTION */}
              <motion.div
                variants={itemVariants}
                className="mobile-nav-section"
              >
                <h3 className="mobile-nav-title">Account</h3>

                {isAuthenticated ? (
                  <>
                    <motion.div variants={itemVariants}>
                      <Link
                        to="/account"
                        className="mobile-nav-link"
                        onClick={toggleMenu}
                      >
                        My Account
                      </Link>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Link
                        to="/account/orders"
                        className="mobile-nav-link"
                        onClick={toggleMenu}
                      >
                        My Orders
                      </Link>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Link
                        to="/account/wishlist"
                        className="mobile-nav-link"
                        onClick={toggleMenu}
                      >
                        Wishlist
                      </Link>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <button
                        onClick={() => {
                          logout();
                          toggleMenu();
                        }}
                        className="mobile-nav-link mobile-nav-button"
                      >
                        Sign Out
                      </button>
                    </motion.div>
                  </>
                ) : (
                  <>
                    <motion.div variants={itemVariants}>
                      <Link
                        to="/login"
                        className="mobile-nav-link"
                        onClick={toggleMenu}
                      >
                        Sign In
                      </Link>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Link
                        to="/register"
                        className="mobile-nav-link"
                        onClick={toggleMenu}
                      >
                        Create Account
                      </Link>
                    </motion.div>
                  </>
                )}
              </motion.div>

              {/* CUSTOMER CARE */}
              <motion.div
                variants={itemVariants}
                className="mobile-nav-section"
              >
                <h3 className="mobile-nav-title">Customer Care</h3>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/contact"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    Contact Us
                  </Link>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/help/shipping"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    Shipping & Returns
                  </Link>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/help/size-guide"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    Size Guide
                  </Link>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Link
                    to="/help/faq"
                    className="mobile-nav-link"
                    onClick={toggleMenu}
                  >
                    FAQ
                  </Link>
                </motion.div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <TrackOrderButton variant="outline" onClick={toggleMenu} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
